import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UnreadCount } from '@up2d8/contracts';
import { useSession } from '../../shared/session';

/** Boven dit aantal staat er "9+": een teller van drie cijfers past niet en zegt niets meer. */
const MAX_SHOWN = 9;

/**
 * Het belicoon met de teller.
 *
 * Wordt elke halve minuut verversd en bij het terugkomen op het scherm. Niet met een
 * websocket: één klein verzoek per halve minuut is goedkoper dan een open verbinding op een
 * telefoon die de hele dag in een broekzak zit — en het scheelt batterij.
 */
/** Het aantal ongelezen meldingen; gedeeld door de bel en de kopbalk op desktop. */
export function useUnreadNotifications() {
  const { api, has } = useSession();

  return useQuery({
    queryKey: ['unread-count'],
    enabled: has('notifications.inbox.self'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/notifications/unread-count');
      return ((data as UnreadCount | undefined)?.unread ?? 0) as number;
    },
  });
}

export function NotificationBell() {
  const { t } = useTranslation();
  const { has } = useSession();
  const count = useUnreadNotifications();

  if (!has('notifications.inbox.self')) return null;

  const unread = count.data ?? 0;

  return (
    <Link
      to="/app/notifications"
      data-touch-target=""
      data-testid="notification-bell"
      aria-label={
        unread > 0 ? t('notifications.bellWithCount', { count: unread }) : t('notifications.bell')
      }
      className="relative inline-flex items-center justify-center rounded-pill p-2 text-text-secondary hover:bg-surface hover:text-brand-primary"
    >
      <Bell aria-hidden className="h-6 w-6" />
      {unread > 0 && (
        <span
          // `aria-hidden`: het aantal staat al in het label van de link, en een schermlezer
          // hoort het niet twee keer te zeggen.
          aria-hidden
          className="absolute right-0 top-0 min-w-[1.25rem] rounded-pill bg-brand-orange px-1 text-center text-caption font-semibold text-text-on-dark"
        >
          {unread > MAX_SHOWN ? `${MAX_SHOWN}+` : unread}
        </span>
      )}
    </Link>
  );
}
